import React from 'react';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { DETAILS_LABEL, NEEDS_CONFIRMATION_LABEL } from '../constants';
import { serviceLines } from '../utils';
import { CardNameTooltip } from './CardNameTooltip';
import {
    Card,
    CardAside,
    CardBody,
    CardDescription,
    CardGrip,
    CardInfoButton,
    CardTag,
    CardTags,
    CardTitle,
    SERVICE_TAG_COLOR,
} from './blocks';

/**
 * One service card on the split board in services mode: quantity, description and code in the split log's form
 * (`1 × Used Merchandise Haul Away (10488)`), the service type, and "Needs confirmation" when the service has to be
 * confirmed. A service always moves whole, so the card has no divided state. Hovering the name shows it in full, and
 * the info button opens the service's details.
 *
 * @param {{ service: { service_id: string, code?: string|null, description?: string|null, quantity?: number|string|null, type?: string|null, require_confirm?: boolean|null }, actions?: React.ReactNode, onShowDetails?: () => void, tooltipSuppressed?: boolean }} props
 *   `service` is the parent service from the order read. `actions` render under the details, for example the Move to
 *   menu. `onShowDetails` shows the info button and opens the details dialog. `tooltipSuppressed` keeps the name
 *   tooltip shut, for example during a drag.
 */
export const ServiceCard = ({ service, actions = null, onShowDetails, tooltipSuppressed = false }) => {
    const [title] = serviceLines([service]);
    const type = service.type?.trim();
    return (
        <Card service aria-label={title} data-testid={`split-service-card-${service.service_id}`}>
            <CardGrip aria-hidden="true">⋮⋮</CardGrip>
            <CardBody>
                <CardNameTooltip title={title} suppressed={tooltipSuppressed}>
                    <CardTitle>{title}</CardTitle>
                    {type ? <CardDescription>{type}</CardDescription> : null}
                </CardNameTooltip>
                {service.require_confirm ? (
                    <CardTags>
                        <CardTag tagColor={SERVICE_TAG_COLOR}>{NEEDS_CONFIRMATION_LABEL}</CardTag>
                    </CardTags>
                ) : null}
                {actions}
            </CardBody>
            {onShowDetails ? (
                <CardAside>
                    <CardInfoButton size="small" aria-label={`${DETAILS_LABEL}: ${title}`} onClick={onShowDetails}>
                        <InfoOutlinedIcon fontSize="small" />
                    </CardInfoButton>
                </CardAside>
            ) : null}
        </Card>
    );
};

export default ServiceCard;
